import { useState, useEffect, useCallback } from "react"
import Cookies from "js-cookie"
import { Plus, Search, Pencil, Trash2, Loader2, BookOpen, ChevronLeft, ChevronRight, AlertCircle, X, CheckCircle2, XCircle, Upload } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

import { environment } from "@/env/env.prod"
import { apiHeaders } from "@/Auth/api-headers."
import { Prevelage } from "../Scripts/rolePrivelageCheck.js"
import { SubjectImportDialog } from "./SubjectImportDialog"
import { useInstituteLabels } from "@/lib/instituteLabels"

// ── Types ─────────────────────────────────────────────────────────────────────
type Subject = {
    id: string
    subject_code: string
    subject_name: string | null
    created_by_name: string | null
    created_at: number
}

type DuplicateWarning = {
    type: "POSSIBLE_DUPLICATE_NAME"
    existing: { id: string; subject_code: string; subject_name: string | null }
}

type BulkRow = { subjectCode: string; subjectName: string }
type BulkResult = {
    added: { subjectCode: string; subjectName: string | null }[]
    failed: { subjectCode: string; subjectName: string | null; reason: string }[]
}

const PAGE_SIZE = 20
const MAX_BULK = 50
const emptyForm = { subjectCode: "", subjectName: "" }
const emptyBulkRow = (): BulkRow => ({ subjectCode: "", subjectName: "" })

async function apiGet(path: string, params: Record<string, string>) {
    const query = new URLSearchParams(params)
    const res = await fetch(`${environment.BASE_URL}/subjects/${path}?${query}`, { headers: apiHeaders() })
    const data = await res.json()
    if (!data.success) throw new Error(data.message || "Request failed")
    return data
}

async function apiPost(path: string, body: any) {
    const res = await fetch(`${environment.BASE_URL}/subjects/${path}`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify(body),
    })
    const data = await res.json()
    if (!data.success) throw new Error(data.message || "Request failed")
    return data
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SubjectRegistry() {
    const L = useInstituteLabels()
    const canManage = new Prevelage(Cookies.get("role")).canManageSubjects()

    const [subjects, setSubjects] = useState<Subject[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [total, setTotal] = useState(0)

    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<Subject | null>(null)
    const [form, setForm] = useState(emptyForm)
    const [saving, setSaving] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)
    const [duplicateWarning, setDuplicateWarning] = useState<DuplicateWarning | null>(null)

    // ── Bulk add ──────────────────────────────────────────────────────────────
    const [mode, setMode] = useState<"single" | "bulk">("single")
    const [bulkRows, setBulkRows] = useState<BulkRow[]>([emptyBulkRow(), emptyBulkRow()])
    const [bulkResult, setBulkResult] = useState<BulkResult | null>(null)

    const [deleteTarget, setDeleteTarget] = useState<Subject | null>(null)
    const [deleting, setDeleting] = useState(false)

    // ── Excel/CSV import ──────────────────────────────────────────────────────
    const [importOpen, setImportOpen] = useState(false)

    const loadSubjects = useCallback(async (targetPage: number, query: string) => {
        setLoading(true)
        setError(null)
        try {
            const data = await apiGet("listSubjects", {
                search: query,
                page: String(targetPage),
                limit: String(PAGE_SIZE),
            })
            setSubjects(data.subjects || [])
            setHasMore(data.hasMore === true)
            setTotal(data.total || 0)
        } catch (e: any) {
            setError(e.message || "Couldn't load subjects.")
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { loadSubjects(1, "") }, [loadSubjects])

    const handleSearch = () => {
        setPage(1)
        loadSubjects(1, search)
    }

    const goToPage = (next: number) => {
        setPage(next)
        loadSubjects(next, search)
    }

    // ── Create / Edit ─────────────────────────────────────────────────────────
    const openCreate = () => {
        setEditing(null)
        setForm(emptyForm)
        setFormError(null)
        setDuplicateWarning(null)
        setMode("single")
        setBulkRows([emptyBulkRow(), emptyBulkRow()])
        setFormOpen(true)
    }

    const updateBulkRow = (idx: number, field: keyof BulkRow, value: string) => {
        setBulkRows(rows => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r))
    }

    const addBulkRow = () => {
        setBulkRows(rows => rows.length >= MAX_BULK ? rows : [...rows, emptyBulkRow()])
    }

    const removeBulkRow = (idx: number) => {
        setBulkRows(rows => rows.filter((_, i) => i !== idx))
    }

    const handleBulkSave = async () => {
        const entries = bulkRows
            .map(r => ({ subjectCode: r.subjectCode.trim(), subjectName: r.subjectName.trim() || null }))
            .filter(r => r.subjectCode)

        if (entries.length === 0) {
            setFormError("Add at least one subject code.")
            return
        }
        if (entries.length > MAX_BULK) {
            setFormError(`Maximum ${MAX_BULK} subjects per bulk add.`)
            return
        }

        setSaving(true)
        setFormError(null)
        try {
            const result = await apiPost("createSubjectsBulk", { subjects: entries })
            setFormOpen(false)
            setBulkResult({ added: result.added, failed: result.failed })
            await loadSubjects(page, search)
        } catch (e: any) {
            setFormError(e.message || "Bulk save failed.")
        } finally {
            setSaving(false)
        }
    }

    const openEdit = (s: Subject) => {
        setEditing(s)
        setForm({ subjectCode: s.subject_code, subjectName: s.subject_name || "" })
        setFormError(null)
        setDuplicateWarning(null)
        setFormOpen(true)
    }

    const handleSave = async () => {
        if (!form.subjectCode.trim()) {
            setFormError("Subject code is required.")
            return
        }
        setSaving(true)
        setFormError(null)
        setDuplicateWarning(null)
        try {
            if (editing) {
                await apiPost("updateSubject", {
                    id: editing.id,
                    subjectCode: form.subjectCode.trim(),
                    subjectName: form.subjectName.trim() || null,
                })
            } else {
                const result = await apiPost("createSubject", {
                    subjectCode: form.subjectCode.trim(),
                    subjectName: form.subjectName.trim() || null,
                })
                if (result.warning) {
                    // Non-blocking — the subject was still created. Surface
                    // the heads-up and let the admin decide whether to
                    // clean it up (edit/delete) themselves.
                    setDuplicateWarning(result.warning)
                }
            }
            setFormOpen(false)
            await loadSubjects(page, search)
        } catch (e: any) {
            setFormError(
                e.message === "DUPLICATE_CODE"
                    ? "A subject with this code already exists."
                    : e.message || "Save failed."
            )
        } finally {
            setSaving(false)
        }
    }

    // ── Delete ────────────────────────────────────────────────────────────────
    const confirmDelete = async () => {
        if (!deleteTarget) return
        const target = deleteTarget
        setDeleteTarget(null)
        setDeleting(true)
        try {
            await apiPost("deleteSubject", { id: target.id })
            await loadSubjects(page, search)
        } catch (e: any) {
            setError(e.message || "Delete failed.")
        } finally {
            setDeleting(false)
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <BookOpen className="h-6 w-6" /> Subject Registry
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        The shared subject code/name list used across attendance registers and timetables.
                        {!canManage && " You have view-only access — only admins can add, edit, or remove subjects."}
                    </p>
                </div>
                {canManage && (
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-1.5">
                            <Upload className="h-4 w-4" /> Import
                        </Button>
                        <Button onClick={openCreate} className="gap-1.5">
                            <Plus className="h-4 w-4" /> Add Subject
                        </Button>
                    </div>
                )}
            </div>

            <Card className="border border-border/60 shadow-sm mb-4">
                <CardContent className="pt-6">
                    <div className="flex gap-2">
                        <Input
                            placeholder="Search by code or name…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSearch() }}
                        />
                        <Button variant="outline" onClick={handleSearch} className="gap-1.5 shrink-0">
                            <Search className="h-4 w-4" /> Search
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card className="border border-border/60 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-base font-semibold">{L.subjects}</CardTitle>
                    <CardDescription>{total} subject{total === 1 ? "" : "s"} registered</CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <p className="text-sm text-red-600 mb-4 flex items-center gap-1.5">
                            <AlertCircle className="h-3.5 w-3.5" /> {error}
                        </p>
                    )}

                    <div className="rounded-lg border border-border/60 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Code</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Added by</TableHead>
                                    {canManage && <TableHead className="text-right">Action</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && (
                                    <TableRow>
                                        <TableCell colSpan={canManage ? 4 : 3} className="text-center text-sm text-muted-foreground py-8">
                                            Loading…
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!loading && subjects.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={canManage ? 4 : 3} className="text-center text-sm text-muted-foreground py-8">
                                            {search ? "No subjects match your search." : "No subjects registered yet."}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!loading && subjects.map(s => (
                                    <TableRow key={s.id}>
                                        <TableCell className="font-medium">{s.subject_code}</TableCell>
                                        <TableCell>{s.subject_name || "—"}</TableCell>
                                        <TableCell className="text-muted-foreground">{s.created_by_name || "—"}</TableCell>
                                        {canManage && (
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button variant="outline" size="sm" className="gap-1" onClick={() => openEdit(s)}>
                                                        <Pencil className="h-3.5 w-3.5" /> Edit
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-1 text-red-600 hover:text-red-700"
                                                        onClick={() => setDeleteTarget(s)}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                        <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)} className="gap-1">
                                <ChevronLeft className="h-3.5 w-3.5" /> Prev
                            </Button>
                            <Button variant="outline" size="sm" disabled={!hasMore || loading} onClick={() => goToPage(page + 1)} className="gap-1">
                                Next <ChevronRight className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* CREATE / EDIT DIALOG */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className={mode === "bulk" ? "sm:max-w-2xl" : undefined}>
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Subject" : "Add Subject"}</DialogTitle>
                    </DialogHeader>

                    {!editing && (
                        <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
                            <Button
                                size="sm"
                                variant={mode === "single" ? "default" : "ghost"}
                                className="h-7 text-xs"
                                onClick={() => { setMode("single"); setFormError(null) }}
                            >
                                Single
                            </Button>
                            <Button
                                size="sm"
                                variant={mode === "bulk" ? "default" : "ghost"}
                                className="h-7 text-xs"
                                onClick={() => { setMode("bulk"); setFormError(null) }}
                            >
                                Bulk add
                            </Button>
                        </div>
                    )}

                    {formError && <p className="text-sm text-red-600">{formError}</p>}

                    {mode === "single" || editing ? (
                        <div className="space-y-4 py-2">
                            <div>
                                <Label>{L.subject} Code</Label>
                                <Input
                                    value={form.subjectCode}
                                    onChange={(e) => setForm(f => ({ ...f, subjectCode: e.target.value }))}
                                    placeholder="e.g. PHY-101"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>{L.subject} Name (optional)</Label>
                                <Input
                                    value={form.subjectName}
                                    onChange={(e) => setForm(f => ({ ...f, subjectName: e.target.value }))}
                                    placeholder="e.g. Physics I"
                                    className="mt-1"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="py-2">
                            <p className="text-xs text-muted-foreground mb-2">
                                Add up to {MAX_BULK} at once. Rows left blank are skipped. Each row is validated
                                independently — one bad row won't block the rest.
                            </p>
                            <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-1">
                                {bulkRows.map((row, idx) => (
                                    <div key={idx} className="flex gap-2 items-start">
                                        <Input
                                            value={row.subjectCode}
                                            onChange={(e) => updateBulkRow(idx, "subjectCode", e.target.value)}
                                            placeholder="Code (e.g. PHY-101)"
                                            className="flex-1"
                                        />
                                        <Input
                                            value={row.subjectName}
                                            onChange={(e) => updateBulkRow(idx, "subjectName", e.target.value)}
                                            placeholder="Name (optional)"
                                            className="flex-1"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="shrink-0 text-muted-foreground hover:text-red-600"
                                            onClick={() => removeBulkRow(idx)}
                                            disabled={bulkRows.length <= 1}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-3 gap-1.5"
                                onClick={addBulkRow}
                                disabled={bulkRows.length >= MAX_BULK}
                            >
                                <Plus className="h-3.5 w-3.5" /> Add row
                            </Button>
                            <p className="text-xs text-muted-foreground mt-1">{bulkRows.length} / {MAX_BULK} rows</p>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
                        {mode === "single" || editing ? (
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : "Save"}
                            </Button>
                        ) : (
                            <Button onClick={handleBulkSave} disabled={saving}>
                                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Adding…</> : "Add All"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* BULK ADD RESULTS */}
            <Dialog open={!!bulkResult} onOpenChange={(o) => !o && setBulkResult(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Bulk add results</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                        {bulkResult && bulkResult.added.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-emerald-600 flex items-center gap-1.5 mb-1.5">
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Added ({bulkResult.added.length})
                                </p>
                                <ul className="text-sm space-y-1">
                                    {bulkResult.added.map((a, i) => (
                                        <li key={i} className="text-muted-foreground">
                                            <span className="font-medium text-foreground">{a.subjectCode}</span>
                                            {a.subjectName ? ` — ${a.subjectName}` : ""}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {bulkResult && bulkResult.failed.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-red-600 flex items-center gap-1.5 mb-1.5">
                                    <XCircle className="h-3.5 w-3.5" /> Failed ({bulkResult.failed.length})
                                </p>
                                <ul className="text-sm space-y-1">
                                    {bulkResult.failed.map((f, i) => (
                                        <li key={i} className="text-muted-foreground">
                                            <span className="font-medium text-foreground">{f.subjectCode}</span> — {f.reason}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setBulkResult(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* NON-BLOCKING DUPLICATE-NAME WARNING */}
            <Dialog open={!!duplicateWarning} onOpenChange={(o) => !o && setDuplicateWarning(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Heads up — possible duplicate</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Your subject was created, but a subject with a very similar name already exists:{" "}
                        <span className="font-semibold text-foreground">
                            {duplicateWarning?.existing.subject_code} — {duplicateWarning?.existing.subject_name}
                        </span>
                        . If this was meant to be the same subject, consider editing or removing one of them.
                    </p>
                    <DialogFooter>
                        <Button onClick={() => setDuplicateWarning(null)}>Got it</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* DELETE CONFIRMATION */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(next) => !next && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{deleteTarget?.subject_code}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the subject from the registry. Existing attendance registers and timetable
                            entries that already reference it are unaffected — it just stops appearing as a
                            selectable option going forward. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
                            {deleting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Deleting…</> : "Delete Subject"}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* EXCEL/CSV IMPORT */}
            <SubjectImportDialog
                open={importOpen}
                onClose={() => { setImportOpen(false); loadSubjects(page, search) }}
            />
        </div>
    )
}
